import { useEffect, useState } from 'react'
import { useConfig, useCreateCustomer, useCustomer, useUpdateCustomer } from 'api'
import { Customer, CustomerFormSchema, Feature } from 'types'
import { Loading, PageContentWrapper, PageError, RWContainer, RWElement, VerticalContainer } from 'components/styled'
import { CloudUploadOutlined } from '@ant-design/icons'
import { getCustomerName, getShopId, NullableField, queryClient, useTranslate, useWindowDimensions, validateFormValues } from 'utils'
import { useNavigate, useParams } from 'react-router-dom'
import { message, theme, Typography } from 'antd'
import { PageHeader } from 'components/page-header/page-header'
import { Form } from 'react-final-form'
import { FormSection } from 'components/form-section/form-section'
import { ImageUpload } from 'components/image-upload/image-upload'
import { FormInput } from 'components/form-inputs'
import { CustomerRemove } from 'components/customer/customer-remove/customer-remove'
const { Title, Text } = Typography

export const CustomerForm = () => {

    const params = useParams()
    const navigate = useNavigate()
    const { data: config } = useConfig()

    useEffect(() => {
        if(config && !config.features.includes(Feature.CUSTOMER_MANAGEMENT)){
            navigate('/')
        }
    }, [config])

    return params.id ? (
        <EditCustomer id={params.id}/>
    ) : (
        <PageContentWrapper>
            <CustomerFormController customer={null} onBack={() => navigate('/customer')}/>
        </PageContentWrapper>
    )
    
}

type EditCustomerParams = { id: string }
const EditCustomer = ({ id }: EditCustomerParams) => {
    const navigate = useNavigate()
    const { data: customer, isLoading } = useCustomer(id, true, () => navigate('/customer'))
    return isLoading ? <Loading/> : !customer ? <PageError/> : (
        <PageContentWrapper>
            <CustomerFormController customer={customer} onBack={() => navigate('/customer')}/>
        </PageContentWrapper>
    )
}

type CustomerControllerProps = {
    customer: Customer | null,
    onCreate?: (customer: Customer) => void
    onBack: () => void
}
export const CustomerFormController = ({ customer, onCreate, onBack }: CustomerControllerProps) => {

    const isCreate = !customer

    //hooks:
    const { token: { padding, marginXS, margin, marginLG } } = theme.useToken()
    const { SM } = useWindowDimensions()
    const { __ } = useTranslate()

    //state:
    const [ initialValues, setInitialValues ] = useState(customer ?? getCustomerDefaultValues())
    const [ savingValues , setSavingValues  ] = useState(customer ?? getCustomerDefaultValues())

    //api:
    const { mutate: createCustomer, isLoading: isCreateLoading, data: createResponse } = useCreateCustomer(savingValues)
    const { mutate: updateCustomer, isLoading: isUpdateLoading, data: updateResponse } = useUpdateCustomer(savingValues)

    const create = (values: Customer) => {
        if(values.email || values.phone){
            setSavingValues(values)
            createCustomer()
        }else{
            message.error(__`at_least_one_contact_email_or_phone_required`)
        }
    }
    const update = (values: Customer) => {
        if(values.email || values.phone){
            setSavingValues(values)
            updateCustomer()
        }else{
            message.error(__`at_least_one_contact_email_or_phone_required`)
        }
    }

    //after create:
    useEffect(() => {
        if(createResponse){
            if(createResponse === 'customer_exists'){
                message.error(__`email_and_phone_have_to_be_unique`)
            }else{
                queryClient.invalidateQueries(['customers', getShopId()])
                message.success(__`customer_added`)
                onCreate?.(createResponse)
                onBack()
            }
        }
    }, [isCreateLoading])

    //after update:
    useEffect(() => {
        if(updateResponse === true){
            setInitialValues({ ...savingValues })
            const shopId = getShopId()
            queryClient.invalidateQueries(['customer', savingValues.id])
            queryClient.invalidateQueries(['currentCustomer'])
            queryClient.invalidateQueries(['customers', shopId])
            queryClient.invalidateQueries(['resources', shopId])
            queryClient.invalidateQueries(['services', shopId])
            queryClient.resetQueries(['service'])
            message.success(__`customer_updated`)
        }else if(updateResponse === 'customer_exists'){
            message.error(__`email_and_phone_have_to_be_unique`)
        }
    }, [isUpdateLoading])

    return (
        <Form
            initialValues={initialValues}
            subscription={{ dirty: true }}
            validate={validateFormValues(CustomerFormSchema())}
            onSubmit={values => (
                CustomerFormSchema().validate(values).then(
                    data => isCreate ? create(data as Customer) : update(data as Customer)
                )
            )}
            render={({ form, dirty, handleSubmit }) => (

                <VerticalContainer style={{ gap: margin }}>

                    {/* header */}
                    <PageHeader
                        title={isCreate ? __`add_new_customer` : getCustomerName(initialValues)}
                        description={__`create_or_manage_your_customers`}
                        primaryButton={{
                            name: isCreate ? __`add` : __`save`,
                            icon: <CloudUploadOutlined/>,
                            mode: (isCreateLoading || isUpdateLoading) ? 'loading' : dirty ? 'normal': 'disabled',
                            onClick: handleSubmit
                        }}
                        secondaryButton={dirty ? {
                            name: __`discard`,
                            mode: (isCreateLoading || isUpdateLoading) ? 'disabled' : 'normal',
                            onClick: () => form.reset()
                        } : undefined}
                        backButton={{
                            name: __`back`,
                            onClick: onBack
                        }}
                        isFormDirty={dirty}
                        shouldSkipUnsavedWarn={createResponse as boolean}
                    />
                    
                    <VerticalContainer style={{ padding: SM ? `0 ${padding}px` : 0, gap: marginLG }}>
            
                        <FormSection style={{ minHeight: 0, paddingBottom: SM ? marginLG : 0 }}>
                        
                                <RWContainer style={{ alignItems: 'normal', gap: marginLG }}>
                    
                                    {/* info */}
                                    <RWElement breakPoint={300}>
                                        <CustomerInfoForm/>
                                    </RWElement>
                    
                                    {/* image */}
                                    <RWElement breakPoint={300}>
                    
                                        <VerticalContainer style={{ height: '100%', gap: marginXS }}>
                    
                                            <Text style={{ fontFamily: 'inter-medium' }}>
                                                {__`image`}
                                            </Text>
                    
                                            <ImageUpload
                                                type='customer'
                                                nameField='name'
                                                imageField='image'
                                            />
                    
                                        </VerticalContainer>
                    
                                    </RWElement>
                    
                                </RWContainer>
                    
                            </FormSection>

                            {isCreate ? null : <CustomerRemove/>}

                    </VerticalContainer>

                </VerticalContainer>

            )}
        />
    )
    
}

const CustomerInfoForm = () => {

    //hooks:
    const { __ } = useTranslate()
    const { token: { paddingXXS, marginXXS, marginSM } } = theme.useToken()
    const { SM } = useWindowDimensions()

    return (
        <VerticalContainer style={{ gap: marginSM }}>

            {/* title */}
            <Title
                level={5}
                style={{
                    paddingTop: SM ? 0 : paddingXXS,
                    margin: `${marginXXS}px 0`
                }}
            >
                {__`info`}
            </Title>

            <NullableField
                name="firstName"
                label={__('optional_label', { label: __`first_name` })}
                placeholder={'John'}
                component={FormInput}
            />

            <NullableField
                name="lastName"
                label={__('optional_label', { label: __`last_name` })}
                placeholder={'Connor'}
                component={FormInput}
            />

            <NullableField
                name="email"
                label={__('optional_label', { label: __`email` })}
                placeholder="customer@email.com"
                component={FormInput}
            />

            <NullableField
                name="phone"
                label={__('optional_label', { label: __`phone` })}
                placeholder={__`phone_example`}
                component={FormInput}
            />

            <NullableField
                name="description"
                label={__('optional_label', { label: __`description` })}
                placeholder={__`prefers_morning_appointments`}
                component={FormInput}
            />

        </VerticalContainer>
    )

}

export const getCustomerDefaultValues = (): Customer => ({
    id: '',
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    image: {
        id: null,
        path: null
    },
    description: null,
    externalId: null,
    externalSource: null
})
